import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion2';
import { Button } from '@/components/ui/button';
import { InputLabel } from '@/components/ui/InputLabel';
import { Label } from '@/components/ui/label';
import Title from '@/components/ui/Title';
import { Trash2, X } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import React from 'react';
import {
  addBannedWords,
  importBannedWordsFromCsv,
  removeBannedWords,
} from '@/features/webinar/webinarThunks';
import { toast } from 'react-toastify';
import { cn } from '@/lib/utils';
import { Pagination } from '@/components/ui/pagination';
import { useTranslation } from 'react-i18next';
import { mapErrorMessage } from '@/lib/mapApiError';

const BANNED_WORDS_PAGE_SIZE = 10;
const MAX_BANNED_WORDS = 500;
const MAX_BANNED_WORD_LENGTH = 50;
const WORDS_REQUEST_BATCH_SIZE = 200;

function chunk<T>(items: T[], size: number): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }
  return batches;
}

type BanWordRow = { id: string; index: number; word: string };

const BAN_WORDS_ACCORDION_VALUE = 'item-1';

export default function BanWordsForm({
  isEdit,
  openAccordionTrigger,
  onAccordionOpened,
}: {
  isEdit?: boolean;
  openAccordionTrigger?: boolean;
  onAccordionOpened?: () => void;
}) {
  const dispatch = useAppDispatch();
  const [accordionValue, setAccordionValue] = React.useState<string>('');

  React.useEffect(() => {
    if (openAccordionTrigger) {
      setAccordionValue(BAN_WORDS_ACCORDION_VALUE);
      onAccordionOpened?.();
    }
  }, [openAccordionTrigger, onAccordionOpened]);
  const bannedWords = useAppSelector(
    (state: RootState) => state.webinar.currentWebinar?.bannedWords,
  );
  const currentWebinar = useAppSelector((state: RootState) => state.webinar.currentWebinar);
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);

  const rows = React.useMemo<BanWordRow[]>(
    () => (bannedWords ?? []).map((word, index) => ({ id: String(index), index, word })),
    [bannedWords],
  );

  const [pageNumber, setPageNumber] = React.useState(0);

  const totalPages = Math.max(1, Math.ceil(rows.length / BANNED_WORDS_PAGE_SIZE));

  React.useEffect(() => {
    const maxPage = Math.max(0, totalPages - 1);
    setPageNumber((p) => (p > maxPage ? maxPage : p));
  }, [totalPages]);

  const paginatedRows = React.useMemo(() => {
    const start = pageNumber * BANNED_WORDS_PAGE_SIZE;
    return rows.slice(start, start + BANNED_WORDS_PAGE_SIZE);
  }, [rows, pageNumber]);

  const [newWord, setNewWord] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const csvImportInputRef = React.useRef<HTMLInputElement>(null);
  const { t } = useTranslation('createWebinar');
  const deleteDisabled = isSaving || !currentWebinar || workspaceId == null;

  const resolveApiErrorMessage = React.useCallback(
    (error: unknown): string => {
      const err = error as {
        message?: string;
        code?: string;
        status?: number;
        details?: unknown;
      };
      const details = err?.details;
      const nestedMessage = Array.isArray(details)
        ? (
            details.find(
              (d): d is { message: string } =>
                !!d &&
                typeof d === 'object' &&
                typeof (d as { message?: unknown }).message === 'string',
            ) as { message: string } | undefined
          )?.message
        : undefined;
      if (nestedMessage?.includes('BANNED_WORDS.TOO_MANY')) {
        return t('banWordsForm.tooManyWords');
      }
      const errorMessage = mapErrorMessage(err?.message, err?.code, err?.status);
      return t(errorMessage) || t('errors.generic');
    },
    [t],
  );

  const handleDeleteWord = React.useCallback(
    async (rowId: string) => {
      if (!currentWebinar || workspaceId == null) return;
      const index = Number(rowId);
      if (Number.isNaN(index)) return;
      const list = bannedWords ?? [];
      if (index < 0 || index >= list.length) return;
      const word = list[index];
      setDeletingId(rowId);
      try {
        await dispatch(
          removeBannedWords({
            workspaceId,
            id: currentWebinar.id,
            words: [word],
          }),
        ).unwrap();
      } catch (err: unknown) {
        toast.error(resolveApiErrorMessage(err));
      } finally {
        setDeletingId(null);
      }
    },
    [bannedWords, currentWebinar, dispatch, workspaceId, resolveApiErrorMessage],
  );

  const columns = React.useMemo<ColumnDef<BanWordRow>[]>(
    () => [
      {
        accessorKey: 'word',
        header: t('banWordsForm.wordLabel'),
        cell: ({ row }) => <span>{row.original.word}</span>,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const id = row.original.id;
          const isDeletingRow = deletingId === id;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void handleDeleteWord(id);
              }}
              disabled={isDeletingRow || deleteDisabled}
              className="p-1 rounded cursor-pointer text-error disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('banWordsForm.deleteWord')}
            >
              <X size={18} />
            </button>
          );
        },
        enableSorting: false,
      },
    ],
    [deleteDisabled, deletingId, handleDeleteWord, t],
  );

  const table = useReactTable({
    data: paginatedRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const handleAddWord = async () => {
    if (!currentWebinar || workspaceId == null) return;
    const trimmed = newWord.trim();
    if (!trimmed) return;
    const existing = bannedWords ?? [];
    if (existing.includes(trimmed)) {
      toast.info(t('banWordsForm.duplicateWord'));
      return;
    }
    if (existing.length >= MAX_BANNED_WORDS) {
      toast.error(t('banWordsForm.maxWordsReached', { max: MAX_BANNED_WORDS }));
      return;
    }
    setIsSaving(true);
    try {
      await dispatch(
        addBannedWords({
          workspaceId,
          id: currentWebinar.id,
          words: [trimmed],
        }),
      ).unwrap();
      setNewWord('');
    } catch (err: unknown) {
      toast.error(resolveApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!currentWebinar || workspaceId == null) return;
    const existing = bannedWords ?? [];
    if (existing.length === 0) return;
    setIsSaving(true);
    try {
      for (const batch of chunk(existing, WORDS_REQUEST_BATCH_SIZE)) {
        await dispatch(
          removeBannedWords({
            workspaceId,
            id: currentWebinar.id,
            words: batch,
          }),
        ).unwrap();
      }
    } catch (err: unknown) {
      toast.error(resolveApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportCsvClick = () => {
    csvImportInputRef.current?.click();
  };

  const handleCsvFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !currentWebinar || workspaceId == null) return;

    const existing = bannedWords ?? [];
    const text = await file.text();
    const csvWords = Array.from(
      new Set(
        text
          .split(/[\r\n,;]+/)
          .map((w) => w.trim())
          .filter(Boolean),
      ),
    );
    const newWords = csvWords.filter((w) => !existing.includes(w));
    if (existing.length + newWords.length > MAX_BANNED_WORDS) {
      toast.error(t('banWordsForm.importExceedsLimit', { max: MAX_BANNED_WORDS }));
      return;
    }

    setIsSaving(true);
    try {
      await dispatch(
        importBannedWordsFromCsv({
          workspaceId,
          id: currentWebinar.id,
          file,
        }),
      ).unwrap();
      toast.success(t('banWordsForm.importSuccess'));
    } catch (err: unknown) {
      toast.error(resolveApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={(value) => setAccordionValue(value || '')}
      >
        <AccordionItem
          value={BAN_WORDS_ACCORDION_VALUE}
          className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
        >
          <AccordionTrigger className="">{t('banWordsForm.title')}</AccordionTrigger>
          <AccordionContent
            className={`flex flex-col gap-4 bg-medium rounded-[20px] p-6 ${isEdit ? 'mb-4' : ''}`}
          >
            <Title level={2}>{t('banWordsForm.addWord')}</Title>
            <div className="flex gap-2 items-start">
              <div className="w-full lg:flex-1 lg:min-w-0">
                <InputLabel
                  label={t('banWordsForm.wordLabel')}
                  className="w-full"
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddWord();
                    }
                  }}
                  maxLength={MAX_BANNED_WORD_LENGTH}
                  disabled={isSaving || !currentWebinar || workspaceId == null}
                />
                <div className="flex justify-end">
                  <p className="text-xs text-secondary">
                    {newWord.length}/{MAX_BANNED_WORD_LENGTH}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                className="h-14 px-10"
                onClick={() => void handleAddWord()}
                disabled={
                  isSaving ||
                  //   !newWord.trim() ||
                  !currentWebinar ||
                  workspaceId == null
                }
              >
                {t('banWordsForm.addWordButton')}
              </Button>
            </div>
            <p className="text-sm text-secondary flex items-center gap-1">
              {t('banWordsForm.importCsvOr')}
              <input
                ref={csvImportInputRef}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                tabIndex={-1}
                onChange={(e) => void handleCsvFileChange(e)}
              />
              <button
                type="button"
                onClick={() => handleImportCsvClick()}
                disabled={isSaving || !currentWebinar || workspaceId == null}
                className="cursor-pointer text-[#2356CC] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('banWordsForm.importCsv')}
              </button>
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <Title level={2} className="text-secondary">
            {t('banWordsForm.listTitle')}
          </Title>
          <div className="flex items-center gap-2 ">
            <button
              type="button"
              onClick={() => void handleDeleteAll()}
              disabled={isSaving || rows.length === 0 || !currentWebinar || workspaceId == null}
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={24} className="text-secondary" />
              <Label className="text-secondary cursor-pointer">{t('banWordsForm.deleteAll')}</Label>
            </button>
          </div>
        </div>
      </div>
      <div className="overflow-hidden border border-[#FBFBFB] rounded-2xl">
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      header.column.id === 'word' && 'text-left py-4 pl-4 pr-4',
                      header.column.id === 'actions' && 'w-14 py-4 pr-4',
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="h-24 text-center text-secondary">
                  {t('banWordsForm.empty')}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(cell.column.id === 'word' && 'text-left py-4 pl-4 pr-4')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="w-full">
          <Pagination
            currentPage={pageNumber}
            totalPages={totalPages}
            onPageChange={setPageNumber}
          />
        </div>
      </div>
    </div>
  );
}
